import { useEffect, useRef, useState } from 'react'

import {
  type TRichMenuCurrentResponse,
} from 'types'

type CreateRichMenuImageFileResult = { file: File }

function richMenuCurrentImageFileName(contentType: string): string {
  const mime = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
  const extRaw = mime.split('/')[1]?.trim().toLowerCase() ?? ''

  return ['rich-menu-current', extRaw].join('.')
}

function buildRichMenuCurrentImageHydrationKey(
  data: TRichMenuCurrentResponse,
): string | null {
  const { imageBase64, imageContentType, lineChannelId, richMenuId } = data
  if (!imageBase64 || !imageContentType) return null

  return `${lineChannelId}-${richMenuId ?? 'none'}-${imageBase64.length}-${imageContentType}`
}

function createRichMenuImageFileFromApiBase64(params: {
  base64: string
  contentType: string
}): CreateRichMenuImageFileResult {
  const bytes = Uint8Array.from(atob(params.base64), (c) => c.charCodeAt(0))
  const blob = new Blob([bytes], { type: params.contentType })
  const file = new File(
    [blob],
    richMenuCurrentImageFileName(params.contentType),
    { type: blob.type },
  )

  return { file }
}

export type UseRichMenuImageFileFromCurrentApiResult = {
  file: File | null
}

/**
 * When GET current rich menu returns image bytes, builds a `File` and syncs it
 * into local state (e.g. for submit / validation) without duplicating work for
 * the same API payload.
 */
export function useRichMenuImageFileFromCurrentApi(
  menuData: TRichMenuCurrentResponse | undefined,
): UseRichMenuImageFileFromCurrentApiResult {
  const [result, setResult] = useState<UseRichMenuImageFileFromCurrentApiResult>({
    file: null,
  })

  const lastHydrationKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!menuData) {
      setResult({ file: null })
      lastHydrationKeyRef.current = null

      return
    }

    const key = buildRichMenuCurrentImageHydrationKey(menuData)

    if (lastHydrationKeyRef.current === key) return

    lastHydrationKeyRef.current = key
    const base64 = menuData.imageBase64
    const contentType = menuData.imageContentType

    if (!base64 || !contentType) {
      lastHydrationKeyRef.current = null
      setResult({ file: null })

      return
    }

    const result = createRichMenuImageFileFromApiBase64({
      base64,
      contentType,
    })

    if (result.file) {
      setResult({ file: result.file })

      return
    }

    lastHydrationKeyRef.current = null
    setResult({ file: null })
  }, [menuData])

  return result
}
